import { Button } from "./ui";
import { formatNumber } from "../lib/format";

/**
 * Offset paging rather than cursors: operator lists are small, always sorted by
 * creation time, and being able to jump around matters more than consistency
 * under concurrent writes.
 *
 * Two ways to say how far the list goes. Most endpoints return a `total` and
 * get a full "1–25 of 340". The audit log cannot: it grows without bound and a
 * `COUNT(*)` over it would get slower forever, so it sends `hasMore` instead
 * and gets the same controls with an open-ended range. Pass one or the other.
 */
export function Pagination({
  total,
  hasMore,
  limit,
  offset,
  onChange,
}: {
  total?: number;
  hasMore?: boolean;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
}) {
  const counted = total !== undefined;

  // Nothing to page through: one short page, and no earlier one behind it.
  if (counted ? total <= limit : !hasMore && offset === 0) return null;

  const from = offset + 1;
  const to = counted ? Math.min(offset + limit, total) : offset + limit;
  const atEnd = counted ? to >= total : !hasMore;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-sm">
      <p className="text-fg-subtle">
        {formatNumber(from)}–{formatNumber(to)}
        {counted && ` of ${formatNumber(total)}`}
      </p>
      <div className="flex w-full gap-2 sm:w-auto">
        <Button
          variant="secondary"
          size="md"
          disabled={offset === 0}
          className="flex-1 sm:flex-none"
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          size="md"
          disabled={atEnd}
          className="flex-1 sm:flex-none"
          onClick={() => onChange(offset + limit)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
